/**
 * Retry Utilities
 *
 * Provides retry logic and data validation utilities to improve
 * reliability of data operations and ensure data consistency.
 */
/**
 * Retry wrapper for async operations
 * @param operation The async operation to retry
 * @param maxRetries Maximum number of retry attempts
 * @param delayMs Delay between retries in milliseconds
 * @returns Promise that resolves with the operation result
 */
export const withRetry = async (operation, maxRetries = 3, delayMs = 1000) => {
    let lastError;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        }
        catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
            if (attempt === maxRetries) {
                console.error(`❌ Operation failed after ${maxRetries} attempts:`, lastError);
                throw lastError;
            }
            console.warn(`⚠️ Operation failed (attempt ${attempt}/${maxRetries}), retrying in ${delayMs}ms:`, lastError.message);
            // Exponential backoff
            const delay = delayMs * Math.pow(2, attempt - 1);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
    throw lastError;
};
/**
 * Validate data consistency across Redux state
 * @param state The Redux state to validate
 * @returns Object containing validation results
 */
export const validateDataConsistency = (state) => {
    const issues = [];
    const warnings = [];
    // Check user data consistency
    if (state.auth?.user && state.student?.current_student) {
        const authUser = state.auth.user;
        const studentData = state.student.current_student;
        if (authUser.id !== studentData.id) {
            issues.push(`User ID mismatch: auth.user.id (${authUser.id}) !== student.current_student.id (${studentData.id})`);
        }
        if (authUser.email !== studentData.email) {
            warnings.push(`Email mismatch: auth.user.email (${authUser.email}) !== student.current_student.email (${studentData.email})`);
        }
    }
    // Check notes data consistency
    if (state.notes?.items && Array.isArray(state.notes.items)) {
        const notes = state.notes.items;
        const user = state.auth?.user;
        if (user?.id) {
            const userNotes = notes.filter((note) => note.student_id === user.id);
            const otherNotes = notes.filter((note) => note.student_id !== user.id);
            if (otherNotes.length > 0) {
                warnings.push(`Found ${otherNotes.length} notes not belonging to current user`);
            }
        }
    }
    // Check lessons data consistency
    if (state.lessons?.items && Array.isArray(state.lessons.items)) {
        const lessons = state.lessons.items;
        const user = state.auth?.user;
        if (user?.id && user.role === 'tutor') {
            const userLessons = lessons.filter((lesson) => lesson.created_by === user.id);
            const otherLessons = lessons.filter((lesson) => lesson.created_by !== user.id);
            if (otherLessons.length > 0) {
                warnings.push(`Found ${otherLessons.length} lessons not created by current tutor`);
            }
        }
    }
    return {
        isValid: issues.length === 0,
        issues,
        warnings,
        timestamp: new Date().toISOString()
    };
};
/**
 * Data validation middleware for Redux actions
 * @param store Redux store
 * @returns Middleware function
 */
export const dataValidationMiddleware = (store) => (next) => (action) => {
    const result = next(action);
    // Validate data consistency after state changes
    if (action.type && typeof action.type === 'string' && action.type.endsWith('/fulfilled')) {
        const state = store.getState();
        const validation = validateDataConsistency(state);
        if (!validation.isValid) {
            console.error('❌ Data consistency issues detected:', validation.issues);
        }
        if (validation.warnings.length > 0) {
            console.warn('⚠️ Data consistency warnings:', validation.warnings);
        }
    }
    return result;
};
/**
 * Debounce function for data refresh operations
 * @param func Function to debounce
 * @param wait Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};
/**
 * Throttle function for data refresh operations
 * @param func Function to throttle
 * @param limit Time limit in milliseconds
 * @returns Throttled function
 */
export const throttle = (func, limit) => {
    let inThrottle;
    return (...args) => {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
};
